import React from 'react';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import useAuth from '../../hooks/use-auth';
import useURLQuery from '../../hooks/use-url-query';
import { redeemPartnerCode } from '../../api/partner-codes';
import { ROUTES } from '../../routing';
import {
  PARTNER_CODE_QUERYPARAM,
  forgetPendingPartnerCode,
  readPendingPartnerCode,
  rememberPendingPartnerCode,
} from './pending-partner-code';

type RequestError = {
  response?: { status?: number; data?: { message?: string } };
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

export default function RedeemPartnerCode() {
  const { isAuthenticated, getTokenSilently } = useAuth();
  const history = useHistory();
  const urlQuery = useURLQuery();
  const queryClient = useQueryClient();
  const attempted = React.useRef(false);

  const codeInUrl = urlQuery.get(PARTNER_CODE_QUERYPARAM)?.trim();
  const code = codeInUrl || readPendingPartnerCode();

  // Login can finish in another tab, which loses the query string.
  React.useEffect(() => {
    if (!isAuthenticated && codeInUrl) {
      rememberPendingPartnerCode(codeInUrl);
    }
  }, [isAuthenticated, codeInUrl]);

  React.useEffect(() => {
    if (!isAuthenticated || !code || attempted.current) {
      return;
    }
    attempted.current = true;

    const removeQueryParam = () => {
      if (urlQuery.has(PARTNER_CODE_QUERYPARAM)) {
        urlQuery.delete(PARTNER_CODE_QUERYPARAM);
        history.replace({ search: urlQuery.toString() });
      }
    };

    (async () => {
      try {
        const token = await getTokenSilently();
        const result = await redeemPartnerCode(token, code);
        forgetPendingPartnerCode();

        const until = result.proUntil
          ? ` until ${formatDate(result.proUntil)}`
          : '';
        toast.success(
          result.alreadyRedeemed
            ? `Your access code is already active on your account${until}.`
            : `Your access code is active. You have full access${until}.`
        );

        // Refetch first, or Home sends the learner back to the plan upsell.
        await queryClient.invalidateQueries('prosumer-profile');
        history.push(ROUTES.HOME.getPath());
      } catch (error) {
        const { response } = error as RequestError;
        // Keep the code only when a retry could succeed.
        if (response?.status && response.status < 500) {
          forgetPendingPartnerCode();
        }
        toast.error(
          response?.data?.message ??
            'Sorry, we could not activate your access code. Please try again or contact support.'
        );
        removeQueryParam();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, code]);

  return null;
}
